import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mantine/core';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import shippingService from '../services/shipping';
import billingService from '../services/billing';
import paymentMethodsService from '../services/payment_methods';
import businessUpgradeService from '../services/business_upgrade';
import { useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import AddressSearch from '../components/AddressSearch';
import AddressForm from '../components/AddressForm';
import AddressList from '../components/AddressList';
import useAddressValidation from '../hooks/useAddressValidation';
import './Settings.css';

export default function Settings() {
	const { t } = useTranslation();
	const { user, isAuthenticated } = useAuth();
	const navigate = useNavigate();
	const { validateAddress } = useAddressValidation();
	const [addresses, setAddresses] = useState([]);
	const [billingAddresses, setBillingAddresses] = useState([]);
	const [paymentMethods, setPaymentMethods] = useState([]);
	const [upgradeRequest, setUpgradeRequest] = useState(null);
	const [upgradeLoading, setUpgradeLoading] = useState(false);
	const [addingCard, setAddingCard] = useState(false);
	const [deletingCardId, setDeletingCardId] = useState(null);
	const [stripePromise] = useState(() => loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY));
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [savingBilling, setSavingBilling] = useState(false);
	const [error, setError] = useState(null);
	const [notice, setNotice] = useState(null);
	const [form, setForm] = useState({
		full_name: '',
		address_line1: '',
		address_line2: '',
		city: '',
		postal_code: '',
		country: 'Switzerland',
		is_default: false,
	});
	const [billingForm, setBillingForm] = useState({
		full_name: '',
		address_line1: '',
		address_line2: '',
		city: '',
		postal_code: '',
		country: 'Switzerland',
		company_name: '',
		vat_number: '',
		is_default: false,
	});
	const [activeTab, setActiveTab] = useState('shipping');

	const fetchAll = async () => {
		try {
			setLoading(true);
			const [shipData, billData, pmData, upgradeData] = await Promise.all([
				shippingService.getAddresses(),
				billingService.getAddresses(),
				paymentMethodsService.getPaymentMethods().catch(() => []),
				businessUpgradeService.getMyRequest().catch(() => null)
			]);
			setAddresses(shipData);
			setBillingAddresses(billData);
			setPaymentMethods(pmData || []);
			setUpgradeRequest(upgradeData);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => { if (isAuthenticated) fetchAll(); }, [isAuthenticated]);
	
// Stripe Add Card Form
function AddCardForm({ onSuccess, onCancel }) {
	const stripe = useStripe();
	const elements = useElements();
	const [errorMsg, setErrorMsg] = useState(null);
	const [saving, setSaving] = useState(false);
	const formRef = useRef();

	const handleSubmit = async (e) => {
		e.preventDefault();
		setErrorMsg(null);
		setSaving(true);
		try {
			const { client_secret } = await paymentMethodsService.createSetupIntent();
			const result = await stripe.confirmCardSetup(client_secret, {
				payment_method: { card: elements.getElement(CardElement) }
			});
			if (result.error) {
				setErrorMsg(result.error.message);
			} else {
				setErrorMsg(null);
				if (onSuccess) onSuccess();
			}
		} catch (err) {
			setErrorMsg(err.message);
		} finally {
			setSaving(false);
		}
	};

	return (
		<form ref={formRef} onSubmit={handleSubmit} style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
			<CardElement options={{ hidePostalCode: true }} />
			{errorMsg ? <div style={{ color: 'red', marginTop: 8 }}>{errorMsg}</div> : null}
			<div style={{ display: 'flex', gap: 8 }}>
				<Button type="submit" disabled={!stripe || saving} loading={saving}>
					{saving ? t('saving', 'Saving...') : t('addCard', 'Add Card')}
				</Button>
				<Button variant="subtle" color="gray" type="button" onClick={onCancel}>
					{t('cancel', 'Cancel')}
				</Button>
			</div>
		</form>
	);
}

	const handleAddressSelect = (addressData) => {
		setForm(prev => ({ ...prev, ...addressData }));
	};
	const handleBillingAddressSelect = (addressData) => {
		setBillingForm(prev => ({ ...prev, ...addressData }));
	};
	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
	};
	const handleBillingChange = (e) => {
		const { name, value, type, checked } = e.target;
		setBillingForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
	};
	const handleBillingSubmit = async (e) => {
		e.preventDefault();
		// Billing addresses have no delivery destination, so the city check
		// is skipped (only the country check applies). See useAddressValidation.
		const result = validateAddress(billingForm, 'billing');
		if (!result.ok) {
			setError(result.message);
			return;
		}
		try {
			setSavingBilling(true);
			setError(null);
			const created = await billingService.addAddress(billingForm);
			setBillingAddresses((prev) =>
				[created, ...prev].map((a) => ({ ...a, is_default: a.id === created.id ? created.is_default : a.is_default }))
					.sort((a, b) => Number(b.is_default) - Number(a.is_default))
			);
			setBillingForm({
				full_name: '', address_line1: '', address_line2: '',
				city: '', postal_code: '', country: 'Switzerland',
				company_name: '', vat_number: '', is_default: false,
			});
		} catch (err) {
			setError(err.message);
		} finally {
			setSavingBilling(false);
		}
	};
	const handleSetBillingDefault = async (addr) => {
		try {
			const updated = await billingService.updateAddress(addr.id, { ...addr, is_default: true });
			setBillingAddresses((prev) =>
				prev.map((a) => (a.id === updated.id ? updated : { ...a, is_default: false }))
					.sort((a, b) => Number(b.is_default) - Number(a.is_default))
			);
		} catch (err) {
			setError(err.message);
		}
	};
	const handleBillingDelete = async (id) => {
		try {
			await billingService.deleteAddress(id);
			setBillingAddresses((prev) => prev.filter((a) => a.id !== id));
		} catch (err) {
			setError(err.message);
		}
	};
	const handleSubmit = async (e) => {
		e.preventDefault();
		const deliveryResult = validateAddress(form);
		setNotice(null);
		try {
			setSaving(true);
			setError(null);
			const created = await shippingService.addAddress(form);
			setAddresses((prev) =>
				[created, ...prev].map((a) => ({ ...a, is_default: a.id === created.id ? created.is_default : a.is_default }))
					.sort((a, b) => Number(b.is_default) - Number(a.is_default))
			);
			setForm({
				full_name: '',
				address_line1: '',
				address_line2: '',
				city: '',
				postal_code: '',
				country: 'Switzerland',
				is_default: false,
			});
			if (!deliveryResult.ok) {
				setNotice(t('addressSavedDeliveryUnavailable', 'Address saved. Delivery is not currently available to this address — please select a different delivery address.'));
			}
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
		}
	};
	const handleSetDefault = async (addr) => {
		try {
			const updated = await shippingService.updateAddress(addr.id, { ...addr, is_default: true });
			setAddresses((prev) =>
				prev
					.map((a) => (a.id === updated.id ? updated : { ...a, is_default: false }))
					.sort((a, b) => Number(b.is_default) - Number(a.is_default))
			);
		} catch (err) {
			setError(err.message);
		}
	};
	const handleDelete = async (id) => {
		try {
			await shippingService.deleteAddress(id);
			setAddresses((prev) => prev.filter((a) => a.id !== id));
		} catch (err) {
			setError(err.message);
		}
	};
	const handleUpgradeRequest = async () => {
		try {
			setUpgradeLoading(true);
			setError(null);
			const created = await businessUpgradeService.submitRequest();
			setUpgradeRequest(created);
		} catch (err) {
			setError(err.message);
		} finally {
			setUpgradeLoading(false);
		}
	};
	if (!isAuthenticated) {
		return (
			<div id="settings-page" className="shipping-page">
				<div id="settings-header" className="shipping-header">
					<h1 id="settings-title">{t('settings', 'Settings')}</h1>
					<p id="settings-login-required" className="shipping-tagline">{t('loginRequired', 'Please log in to manage your settings.')}</p>
					<Button fullWidth onClick={() => navigate('/login')}>{t('login', 'Login')}</Button>
				</div>
			</div>
		);
	}
	return (
    <div
      id="settings-page"
      className="dashboard-settings-view dashboard-view-wrapper"
      style={{ padding: "0", maxWidth: "none", margin: "0" }}
    >
      <header id="settings-header" className="dashboard-page-header">
        <div>
          <h1 id="settings-page-title" className="dashboard-page-title">
            {t("settings", "Settings")}
          </h1>
          <p
            id="settings-page-description"
            className="dashboard-page-description"
          >
            {t("settingsDesc", "Manage your personal details")}
          </p>
        </div>
      </header>
      {user?.user_type === 'retail' && (
        <section className="settings-upgrade-section" aria-labelledby="upgrade-section-title">
          {upgradeRequest ? (
            <div className={`upgrade-status upgrade-status--${upgradeRequest.status}`}>
              <p id="upgrade-section-title" className="upgrade-status-text">
                {upgradeRequest.status === 'pending' && t('upgradeStatusPending', 'You have requested to upgrade to a business account. Your request is being reviewed.')}
                {upgradeRequest.status === 'approved' && t('upgradeStatusApproved', 'Your request to upgrade to a business account has been accepted.')}
                {upgradeRequest.status === 'rejected' && t('upgradeStatusRejected', 'Your request to upgrade to a business account has been rejected.')}
              </p>
              {upgradeRequest.status === 'rejected' && upgradeRequest.rejection_reason && (
                <p className="upgrade-rejection-reason">
                  {t('upgradeRejectionReason', 'Reason')}: {upgradeRequest.rejection_reason}
                </p>
              )}
            </div>
          ) : (
            <div className="upgrade-request">
              <div className="upgrade-request-info">
                <h2 id="upgrade-section-title" className="upgrade-request-title">
                  {t('upgradeToBusiness', 'Upgrade to Business Account')}
                </h2>
                <p className="upgrade-request-description">
                  {t('upgradeBenefits', 'Get access to monthly billing and invoicing, dedicated support, and more benefits for your business.')}
                </p>
              </div>
              <Button
                onClick={handleUpgradeRequest}
                disabled={addresses.length === 0 || billingAddresses.length === 0 || upgradeLoading}
                loading={upgradeLoading}
                className="upgrade-request-btn"
                aria-describedby="upgrade-address-requirement"
              >
                {t('requestUpgrade', 'Request Upgrade')}
              </Button>
              {(addresses.length === 0 || billingAddresses.length === 0) && (
                <p id="upgrade-address-requirement" className="upgrade-address-hint">
                  {addresses.length === 0 && billingAddresses.length === 0
                    ? t('upgradeNeedBothAddresses', 'Please add both a shipping and a billing address before requesting an upgrade.')
                    : addresses.length === 0
                      ? t('upgradeNeedShippingAddress', 'Please add a shipping address before requesting an upgrade.')
                      : t('upgradeNeedBillingAddress', 'Please add a billing address before requesting an upgrade.')}
                </p>
              )}
            </div>
          )}
        </section>
      )}
      <div
        className="settings-tabs"
        role="tablist"
        aria-label={t("settingsTabsLabel", "Settings sections")}
        style={{
          marginTop: "1rem",
          display: "flex",
          gap: "1rem",
          borderBottom: "1px solid #eee",
          paddingBottom: "0.5rem",
        }}
      >
        <Button
          variant="subtle"
          color={activeTab === 'shipping' ? 'blue' : 'gray'}
          onClick={() => setActiveTab("shipping")}
          role="tab"
          aria-selected={activeTab === 'shipping'}
          aria-current={activeTab === 'shipping' ? 'page' : undefined}
          id="settings-tab-shipping"
          aria-controls="manage-addresses-section"
          style={{ borderBottom: activeTab === 'shipping' ? '2px solid #000' : 'none', borderRadius: 0 }}
        >
          {t("shippingAddresses", "Shipping Addresses")}
        </Button>
        <Button
          variant="subtle"
          color={activeTab === 'billing' ? 'blue' : 'gray'}
          onClick={() => setActiveTab("billing")}
          role="tab"
          aria-selected={activeTab === 'billing'}
          aria-current={activeTab === 'billing' ? 'page' : undefined}
          id="settings-tab-billing"
          aria-controls="manage-billing-section"
          style={{ borderBottom: activeTab === 'billing' ? '2px solid #000' : 'none', borderRadius: 0 }}
        >
          {t("billingAddresses", "Billing Addresses")}
        </Button>
        <Button
          variant="subtle"
          color={activeTab === 'payment' ? 'blue' : 'gray'}
          onClick={() => setActiveTab("payment")}
          role="tab"
          aria-selected={activeTab === 'payment'}
          aria-current={activeTab === 'payment' ? 'page' : undefined}
          id="settings-tab-payment"
          aria-controls="manage-payment-methods-section"
          style={{ borderBottom: activeTab === 'payment' ? '2px solid #000' : 'none', borderRadius: 0 }}
        >
          {t("savedCards", "Saved Cards")}
        </Button>
      </div>
      <div
        id="settings-content"
        className="shipping-content"
        style={{ padding: 0, boxShadow: "none", marginTop: "1rem" }}
      >
        {error ? (
          <p id="settings-error" style={{ color: "red" }}>
            {error}
          </p>
        ) : null}
        {notice ? (
          <p id="settings-notice" role="status" style={{ color: "#0a7d33", fontWeight: 600 }}>
            {notice}
          </p>
        ) : null}
        {activeTab === "shipping" && (
          <section
            id="manage-addresses-section"
            className="shipping-section"
            role="tabpanel"
            aria-labelledby="settings-tab-shipping"
          >
            <h2 id="manage-addresses-title">
              {t("manageAddresses", "Manage Shipping Addresses")}
            </h2>
            <AddressForm
              form={form}
              onChange={handleChange}
              onSubmit={handleSubmit}
              loading={loading}
              saving={saving}
              type="Shipping"
            >
              <AddressSearch onSelectResult={handleAddressSelect} />
            </AddressForm>
            <AddressList
              addresses={addresses}
              loading={loading}
              onSetDefault={handleSetDefault}
              onDelete={handleDelete}
              type="shipping"
            />
          </section>
        )}
        {activeTab === "billing" && (
          <section
            id="manage-billing-section"
            className="shipping-section"
            role="tabpanel"
            aria-labelledby="settings-tab-billing"
          >
            <h2 id="manage-billing-title">
              {t("manageBillingAddresses", "Manage Billing Addresses")}
            </h2>
            <AddressForm
              form={billingForm}
              onChange={handleBillingChange}
              onSubmit={handleBillingSubmit}
              loading={loading}
              saving={savingBilling}
              type="Billing"
              extraFields={[
                <input
                  key="company"
                  name="company_name"
                  placeholder={t("companyName", "Company Name (Optional)")}
                  value={billingForm.company_name}
                  onChange={handleBillingChange}
                />,
                <input
                  key="vat"
                  name="vat_number"
                  placeholder={t("vatNumber", "VAT Number (Optional)")}
                  value={billingForm.vat_number}
                  onChange={handleBillingChange}
                />,
              ]}
            >
              <AddressSearch
                onSelectResult={handleBillingAddressSelect}
                showDeliveryDisclaimer={false}
              />
            </AddressForm>
            <AddressList
              addresses={billingAddresses}
              loading={loading}
              onSetDefault={handleSetBillingDefault}
              onDelete={handleBillingDelete}
              type="billing"
            />
          </section>
        )}
        {activeTab === "payment" && (
          <section
            id="manage-payment-methods-section"
            className="shipping-section"
            role="tabpanel"
            aria-labelledby="settings-tab-payment"
            style={{ marginTop: "2rem" }}
          >
            <h2 id="manage-payment-methods-title">
              {t("savedCards", "Saved Cards")}
            </h2>
            <p
              className="payment-disclaimer"
              style={{
                fontSize: "0.85rem",
                color: "#666",
                marginBottom: "1rem",
              }}
            >
              {t(
                "paymentDisclaimer",
                "Cards are securely stored by Stripe. We do not store any credit card information on our servers."
              )}
            </p>
            <Elements stripe={stripePromise}>
              {addingCard ? (
                <AddCardForm
                  onSuccess={() => {
                    setAddingCard(false);
                    fetchAll();
                  }}
                  onCancel={() => setAddingCard(false)}
                />
              ) : (
                <Button
                  onClick={() => setAddingCard(true)}
                  mb="md"
                >
                  {t("addCard", "Add Card")}
                </Button>
              )}
            </Elements>
            <div id="payment-methods-list" className="address-list">
              {loading ? (
                <p>{t("loading", "Loading...")}</p>
              ) : paymentMethods.length === 0 ? (
                <p>{t("noSavedCards", "No saved cards found.")}</p>
              ) : (
                paymentMethods.map((pm, index) => (
                  <div
                    key={pm.id}
                    className="address-card"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <strong>{pm.brand.toUpperCase()}</strong>{" "}
                      {t("endingIn", "ending in")} {pm.last4}
                      <div>
                        {t("expires", "Expires")} {pm.exp_month}/{pm.exp_year}
                      </div>
                    </div>
                    <Button
                      variant="light"
                      color="red"
                      size="xs"
                      loading={deletingCardId === pm.id}
                      onClick={async () => {
                        setDeletingCardId(pm.id);
                        try {
                          await paymentMethodsService.deletePaymentMethod(
                            pm.id
                          );
                          fetchAll();
                        } catch (err) {
                          setError(err.message);
                        } finally {
                          setDeletingCardId(null);
                        }
                      }}
                      disabled={deletingCardId === pm.id}
                      style={{ marginLeft: 16 }}
                    >
                      {deletingCardId === pm.id
                        ? t("deleting", "Deleting...")
                        : t("delete", "Delete")}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
// This file was renamed from Addresses.jsx and will be refactored for modularity and DRY principles.
// ...existing code from Addresses.jsx will be placed here...
